// ============================================================
// Customer Dashboard — Alpine components (client-side, token auth)
// Data via /api/v1/account/* & /api/v1/profile
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  preparePublicKeyOptions,
  credentialToJson,
  passkeySupported,
} from '../../helpers/webauthn.js';
import { resolveNotificationWebUrl } from '../../utils/resolveNotificationWebUrl.js';

// Fix default marker icons under Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Default: Padang Panjang, Sumbar */
const DEFAULT_MAP_CENTER = [-0.462002, 100.401102];
const DEFAULT_MAP_ZOOM = 15;

const rp = (n) =>
  window.formatRupiah ? window.formatRupiah(n) : `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

const toast = (msg, type = 'info') => window.toast?.(msg, type);

/** Haversine km (jarak garis lurus) */
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Pastikan token di localStorage cocok dengan profil di server */
async function verifyDashboardSession() {
  if (!window.location.pathname.startsWith('/dashboard')) return;

  const store = Alpine.store('auth');
  if (!store?.isLoggedIn) return;

  try {
    const { data } = await axios.get('/api/v1/profile');
    const serverId = data.data?.id;
    const localId = store.user?.id;

    if (serverId && localId && String(serverId) !== String(localId)) {
      await store.logoutUser({
        redirect: '/login?reason=session_mismatch&redirect=' + encodeURIComponent(window.location.pathname),
      });
      return;
    }

    if (serverId && data.data) {
      store.setAuth({ ...store.user, ...data.data }, store.token);
    }
  } catch (e) {
    if (e.response?.status === 401) {
      store.clearAuth();
      window.location.replace(
        '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search),
      );
    }
  }
}

document.addEventListener('alpine:init', () => {
  verifyDashboardSession();
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted && window.location.pathname.startsWith('/dashboard')) {
    window.location.reload();
  }
});

// ---- Overview ------------------------------------------------
Alpine.data('dashboardOverview', () => ({
  loading: true,
  resending: false,
  data: null,

  async init() { await this.fetch(); },

  async fetch() {
    try {
      const { data } = await axios.get('/api/v1/account/summary');
      this.data = data.data;
    } catch (e) {
      toast('Gagal memuat ringkasan akun.', 'error');
    } finally {
      this.loading = false;
    }
  },

  async resendEmail() {
    this.resending = true;
    try {
      const { data } = await axios.post('/api/v1/auth/email/resend');
      toast(data.message || 'Link verifikasi dikirim ke email Anda.', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal mengirim email verifikasi.', 'error');
    } finally {
      this.resending = false;
    }
  },

  rp,
}));

// ---- Orders list --------------------------------------------
Alpine.data('dashboardOrders', () => ({
  loading: true,
  orders: [],
  meta: null,
  status: 'all',
  page: 1,
  tabs: [
    { k: 'all', l: 'Semua' },
    { k: 'unpaid', l: 'Belum Bayar' },
    { k: 'active', l: 'Berlangsung' },
    { k: 'selesai', l: 'Selesai' },
    { k: 'dibatalkan', l: 'Dibatalkan' },
  ],

  async init() {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('status');
    if (s && this.tabs.some((t) => t.k === s)) this.status = s;
    await this.fetch();
  },

  async setStatus(s) {
    if (this.status === s) return;
    this.status = s;
    this.page = 1;
    await this.fetch();
  },

  async go(p) {
    if (p < 1 || (this.meta && p > this.meta.last_page)) return;
    this.page = p;
    await this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },

  async fetch() {
    this.loading = true;
    try {
      const { data } = await axios.get('/api/v1/account/orders', {
        params: { status: this.status, page: this.page },
      });
      this.orders = data.data || [];
      this.meta = data.meta || null;
    } catch (e) {
      toast('Gagal memuat pesanan.', 'error');
    } finally {
      this.loading = false;
    }
  },

  pay(o) { window.location.href = `/shop/payment/${o.id}`; },
  open(o) { window.location.href = `/dashboard/pesanan/${o.order_number}`; },
}));

// ---- Order detail -------------------------------------------
Alpine.data('dashboardOrderDetail', (cfg = {}) => ({
  cfg,
  loading: true,
  busy: false,
  order: null,

  async init() { await this.fetch(); },

  async fetch() {
    try {
      const { data } = await axios.get(`/api/v1/account/orders/${cfg.orderId}`);
      this.order = data.data;
    } catch (e) {
      toast('Pesanan tidak ditemukan.', 'error');
    } finally {
      this.loading = false;
    }
  },

  pay() { window.location.href = `/shop/payment/${this.order.id}`; },

  async cancelOrder() {
    const ok = await window.confirmDialog({
      title: 'Batalkan Pesanan?',
      message: 'Batalkan pesanan ini? Tindakan tidak bisa dibatalkan.',
      confirmText: 'Ya, Batalkan',
      cancelText: 'Tidak',
      variant: 'warning',
    });
    if (!ok) return;
    this.busy = true;
    try {
      await axios.post(`/api/v1/checkout/${this.order.id}/cancel`, {});
      toast('Pesanan dibatalkan.', 'success');
      await this.fetch();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal membatalkan pesanan.', 'error');
    } finally {
      this.busy = false;
    }
  },

  async reorder() {
    this.busy = true;
    try {
      const { data } = await axios.post(`/api/v1/checkout/${this.order.id}/reorder`);
      toast(data.message || 'Produk ditambahkan ke keranjang.', 'success');
      window.location.href = '/shop/cart';
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal memesan ulang.', 'error');
    } finally {
      this.busy = false;
    }
  },

  invoiceUrl() { return `/invoices/${this.order.order_number}/download`; },

  downloadInvoice() {
    if (!this.order?.order_number) return;
    window.location.href = this.invoiceUrl();
  },
}));

// ---- Frames (published twibbons) ----------------------------
Alpine.data('dashboardFrames', () => ({
  loading: true,
  frames: [],

  async init() { await this.fetch(); },

  async fetch() {
    try {
      const { data } = await axios.get('/api/v1/account/frames');
      this.frames = data.data || [];
    } catch (e) {
      toast('Gagal memuat bingkai.', 'error');
    } finally {
      this.loading = false;
    }
  },

  copy(url) {
    if (!url) return;
    navigator.clipboard?.writeText(url)
      .then(() => toast('Link disalin ke clipboard.', 'success'))
      .catch(() => toast('Gagal menyalin link.', 'error'));
  },
}));

// ---- Creations (invitations, flowers, frame results) --------
Alpine.data('dashboardCreations', () => ({
  loading: true,
  tab: 'invitations',
  data: { invitations: [], flowers: [], frame_results: [] },

  async init() { await this.fetch(); },

  async fetch() {
    try {
      const { data } = await axios.get('/api/v1/account/creations');
      this.data = data.data || this.data;
    } catch (e) {
      toast('Gagal memuat kreasi.', 'error');
    } finally {
      this.loading = false;
    }
  },

  get current() {
    if (this.tab === 'flowers') return this.data.flowers;
    if (this.tab === 'frames') return this.data.frame_results;
    return this.data.invitations;
  },
}));

// ---- Addresses ----------------------------------------------
const emptyAddress = () => ({
  label: '', recipient_name: '', phone: '', address_line: '',
  province_id: '', regency_id: '', district_id: '', village_id: '',
  postal_code: '', lat: null, lng: null, is_default: false,
});

Alpine.data('dashboardAddresses', () => ({
  loading: true,
  saving: false,
  loadingRegions: false,
  locating: false,
  addresses: [],
  storePoint: null,
  routeMeta: {},
  showForm: false,
  editingId: null,
  form: emptyAddress(),
  provinces: [],
  regencies: [],
  districts: [],
  villages: [],
  map: null,
  marker: null,
  showRouteModal: false,
  routeModalLoading: false,
  routeModalAddressId: null,
  routeLabel: '',
  routeResult: null,
  routeMap: null,
  routeLayer: null,

  async init() {
    await Promise.all([this.fetch(), this.loadProvinces(), this.loadStorePoint()]);
    this.$watch('showForm', (open) => {
      if (open) {
        this.$nextTick(() => this.initMap());
      } else {
        this.destroyMap();
      }
    });
    this.$watch('showRouteModal', (open) => {
      if (!open) this.destroyRouteMap();
    });
  },

  formatKm(km) {
    if (km == null || Number.isNaN(Number(km))) return '—';
    const n = Number(km);
    return n < 1 ? `${Math.round(n * 1000)} m` : `${n.toFixed(1)} km`;
  },

  straightKm(a) {
    if (!this.storePoint || a?.lat == null || a?.lng == null) return null;
    return Math.round(haversineKm(Number(a.lat), Number(a.lng), this.storePoint.lat, this.storePoint.lng) * 100) / 100;
  },

  async loadStorePoint() {
    try {
      const { data } = await axios.get('/api/v1/store/location');
      this.storePoint = data.data || null;
    } catch (e) {
      this.storePoint = null;
    }
  },

  async fetch() {
    this.loading = true;
    try {
      const { data } = await axios.get('/api/v1/profile/addresses');
      this.addresses = data.data || [];
      this.prefetchRoutes();
    } catch (e) {
      toast('Gagal memuat alamat.', 'error');
    } finally {
      this.loading = false;
    }
  },

  prefetchRoutes() {
    (this.addresses || []).forEach((a) => {
      if (a?.lat == null || a?.lng == null) return;
      this.loadRouteMeta(a);
    });
  },

  async loadRouteMeta(a) {
    if (!a?.id || a.lat == null || a.lng == null) return;
    this.routeMeta = {
      ...this.routeMeta,
      [a.id]: {
        ...(this.routeMeta[a.id] || {}),
        loading: true,
        straight_km: this.straightKm(a),
      },
    };
    try {
      const { data } = await axios.get('/api/v1/store/access-route', {
        params: { address_id: a.id },
      });
      const r = data.data || {};
      this.routeMeta = {
        ...this.routeMeta,
        [a.id]: {
          loading: false,
          straight_km: r.straight_km ?? this.straightKm(a),
          road_km: r.road_km ?? null,
          duration_min: r.duration_min ?? null,
          warning: r.warning || null,
        },
      };
    } catch (e) {
      this.routeMeta = {
        ...this.routeMeta,
        [a.id]: {
          loading: false,
          straight_km: this.straightKm(a),
          road_km: null,
          duration_min: null,
          warning: e.response?.data?.message || 'Gagal memuat jarak jalan.',
        },
      };
    }
  },

  async openRoute(a) {
    if (!a?.id || a.lat == null || a.lng == null) {
      toast('Alamat belum punya koordinat.', 'error');
      return;
    }
    this.routeModalAddressId = a.id;
    this.routeLabel = `${a.label || 'Alamat'} → USPATIH`;
    this.routeResult = null;
    this.showRouteModal = true;
    this.routeModalLoading = true;
    try {
      const { data } = await axios.get('/api/v1/store/access-route', {
        params: { address_id: a.id },
      });
      this.routeResult = data.data || null;
      const r = this.routeResult || {};
      this.routeMeta = {
        ...this.routeMeta,
        [a.id]: {
          loading: false,
          straight_km: r.straight_km ?? this.straightKm(a),
          road_km: r.road_km ?? null,
          duration_min: r.duration_min ?? null,
          warning: r.warning || null,
        },
      };
      this.$nextTick(() => this.renderRouteMap());
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal memuat jalur.', 'error');
      this.showRouteModal = false;
    } finally {
      this.routeModalLoading = false;
    }
  },

  closeRoute() {
    this.showRouteModal = false;
    this.routeResult = null;
    this.routeModalAddressId = null;
    this.destroyRouteMap();
  },

  renderRouteMap() {
    const el = this.$refs.routeMapEl;
    if (!el || !this.routeResult) return;
    this.destroyRouteMap();

    const origin = this.routeResult.origin || {};
    const store = this.routeResult.store || this.storePoint || {};
    const oLat = Number(origin.lat);
    const oLng = Number(origin.lng);
    const sLat = Number(store.lat);
    const sLng = Number(store.lng);

    this.routeMap = L.map(el, { scrollWheelZoom: true }).setView(
      [oLat || DEFAULT_MAP_CENTER[0], oLng || DEFAULT_MAP_CENTER[1]],
      13
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.routeMap);

    const bounds = [];
    if (!Number.isNaN(oLat) && !Number.isNaN(oLng)) {
      L.marker([oLat, oLng]).addTo(this.routeMap).bindPopup('Alamat Anda');
      bounds.push([oLat, oLng]);
    }
    if (!Number.isNaN(sLat) && !Number.isNaN(sLng)) {
      L.marker([sLat, sLng]).addTo(this.routeMap).bindPopup(store.name || 'USPATIH');
      bounds.push([sLat, sLng]);
    }

    const geometry = this.routeResult.geometry;
    if (geometry && geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
      this.routeLayer = L.geoJSON(
        { type: 'Feature', geometry, properties: {} },
        { style: { color: '#0f766e', weight: 5, opacity: 0.9 } }
      ).addTo(this.routeMap);
      try {
        this.routeMap.fitBounds(this.routeLayer.getBounds(), { padding: [28, 28] });
      } catch (_) {
        if (bounds.length) this.routeMap.fitBounds(bounds, { padding: [40, 40] });
      }
    } else if (bounds.length) {
      this.routeMap.fitBounds(bounds, { padding: [40, 40] });
    }

    setTimeout(() => this.routeMap?.invalidateSize(), 120);
  },

  destroyRouteMap() {
    if (this.routeMap) {
      this.routeMap.remove();
      this.routeMap = null;
      this.routeLayer = null;
    }
  },

  async loadProvinces() {
    try {
      const { data } = await axios.get('/api/v1/regions/provinces');
      this.provinces = data.data || [];
      if (!this.provinces.length) {
        toast('Data wilayah belum tersedia. Hubungi admin.', 'error');
      }
    } catch (e) {
      this.provinces = [];
      toast('Gagal memuat daftar provinsi.', 'error');
    }
  },

  async loadRegencies(provinceId) {
    this.regencies = [];
    this.districts = [];
    this.villages = [];
    if (!provinceId) return;
    this.loadingRegions = true;
    try {
      const { data } = await axios.get('/api/v1/regions/regencies', { params: { province_id: provinceId } });
      this.regencies = data.data || [];
    } catch (e) {
      this.regencies = [];
    } finally {
      this.loadingRegions = false;
    }
  },

  async loadDistricts(regencyId) {
    this.districts = [];
    this.villages = [];
    if (!regencyId) return;
    this.loadingRegions = true;
    try {
      const { data } = await axios.get('/api/v1/regions/districts', { params: { regency_id: regencyId } });
      this.districts = data.data || [];
    } catch (e) {
      this.districts = [];
    } finally {
      this.loadingRegions = false;
    }
  },

  async loadVillages(districtId) {
    this.villages = [];
    if (!districtId) return;
    this.loadingRegions = true;
    try {
      const { data } = await axios.get('/api/v1/regions/villages', { params: { district_id: districtId } });
      this.villages = data.data || [];
    } catch (e) {
      this.villages = [];
    } finally {
      this.loadingRegions = false;
    }
  },

  async onProvinceChange() {
    this.form.regency_id = '';
    this.form.district_id = '';
    this.form.village_id = '';
    await this.loadRegencies(this.form.province_id);
  },

  async onRegencyChange() {
    this.form.district_id = '';
    this.form.village_id = '';
    await this.loadDistricts(this.form.regency_id);
  },

  async onDistrictChange() {
    this.form.village_id = '';
    await this.loadVillages(this.form.district_id);
  },

  initMap() {
    const el = this.$refs.mapEl;
    if (!el || this.map) {
      if (this.map) {
        setTimeout(() => this.map.invalidateSize(), 80);
      }
      return;
    }

    const hasPoint = this.form.lat != null && this.form.lng != null
      && !Number.isNaN(Number(this.form.lat)) && !Number.isNaN(Number(this.form.lng));
    const center = hasPoint
      ? [Number(this.form.lat), Number(this.form.lng)]
      : DEFAULT_MAP_CENTER;

    this.map = L.map(el, { scrollWheelZoom: true }).setView(center, DEFAULT_MAP_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    this.marker = L.marker(center, { draggable: true }).addTo(this.map);
    this.setCoords(center[0], center[1], false);

    this.marker.on('dragend', () => {
      const p = this.marker.getLatLng();
      this.setCoords(p.lat, p.lng);
    });

    this.map.on('click', (e) => {
      this.marker.setLatLng(e.latlng);
      this.setCoords(e.latlng.lat, e.latlng.lng);
    });

    setTimeout(() => this.map?.invalidateSize(), 120);
  },

  destroyMap() {
    if (this.map) {
      this.map.remove();
      this.map = null;
      this.marker = null;
    }
  },

  setCoords(lat, lng, toastMsg = false) {
    this.form.lat = Number(Number(lat).toFixed(6));
    this.form.lng = Number(Number(lng).toFixed(6));
    if (toastMsg) toast('Titik koordinat diperbarui.', 'success');
  },

  useMyLocation() {
    if (!navigator.geolocation) {
      toast('Browser tidak mendukung geolokasi.', 'error');
      return;
    }
    this.locating = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        this.setCoords(lat, lng, true);
        if (this.map && this.marker) {
          this.marker.setLatLng([lat, lng]);
          this.map.setView([lat, lng], 17);
        }
        this.locating = false;
      },
      () => {
        toast('Gagal membaca lokasi. Izinkan akses lokasi di browser.', 'error');
        this.locating = false;
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  },

  openCreate() {
    this.editingId = null;
    this.form = emptyAddress();
    this.regencies = [];
    this.districts = [];
    this.villages = [];
    this.showForm = true;
  },

  async openEdit(a) {
    this.editingId = a.id;
    this.form = {
      label: a.label,
      recipient_name: a.recipient_name,
      phone: a.phone,
      address_line: a.address_line,
      province_id: a.province_id || '',
      regency_id: a.regency_id || '',
      district_id: a.district_id || '',
      village_id: a.village_id || '',
      postal_code: a.postal_code,
      lat: a.lat != null ? Number(a.lat) : null,
      lng: a.lng != null ? Number(a.lng) : null,
      is_default: a.is_default,
    };
    this.showForm = true;
    if (this.form.province_id) await this.loadRegencies(this.form.province_id);
    if (this.form.regency_id) await this.loadDistricts(this.form.regency_id);
    if (this.form.district_id) await this.loadVillages(this.form.district_id);
  },

  async save() {
    if (!this.form.province_id || !this.form.regency_id || !this.form.district_id || !this.form.village_id) {
      toast('Lengkapi provinsi, kota/kabupaten, kecamatan, dan kelurahan.', 'error');
      return;
    }
    if (this.form.lat == null || this.form.lng == null) {
      toast('Tentukan titik koordinat di peta.', 'error');
      return;
    }
    this.saving = true;
    try {
      if (this.editingId) {
        await axios.put(`/api/v1/profile/addresses/${this.editingId}`, this.form);
      } else {
        await axios.post('/api/v1/profile/addresses', this.form);
      }
      toast('Alamat berhasil disimpan.', 'success');
      this.showForm = false;
      await this.fetch();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal menyimpan alamat.', 'error');
    } finally {
      this.saving = false;
    }
  },

  async remove(a) {
    const ok = await window.confirmDialog({
      title: 'Hapus Alamat?',
      message: 'Hapus alamat ini dari daftar Anda?',
      confirmText: 'Ya, Hapus',
      cancelText: 'Batal',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/v1/profile/addresses/${a.id}`);
      toast('Alamat dihapus.', 'success');
      await this.fetch();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal menghapus alamat.', 'error');
    }
  },

  async setDefault(a) {
    if (a.is_default) return;
    try {
      await axios.patch(`/api/v1/profile/addresses/${a.id}/default`);
      await this.fetch();
    } catch (e) {
      toast('Gagal mengubah alamat utama.', 'error');
    }
  },
}));

// ---- Profile ------------------------------------------------
Alpine.data('dashboardProfile', () => ({
  loading: true,
  saving: false,
  savingPw: false,
  uploading: false,
  resending: false,
  profile: null,
  form: { name: '', email: '', phone: '' },
  pw: { current_password: '', new_password: '', new_password_confirmation: '' },
  showPwCurrent: false,
  showPwNew: false,
  showPwConfirm: false,

  passkeySupported: passkeySupported(),
  passkeyEnabled: false,
  passkeys: [],
  passkeysLoading: false,
  canAddPasskey: false,
  passkeyMax: 5,
  showPasskeyForm: false,
  showPasskeyPw: false,
  creatingPasskey: false,
  deletingPasskey: '',
  passkeyForm: { label: '', password: '' },

  async init() {
    await this.fetch();
    await this.fetchPasskeys();
  },

  async fetch() {
    try {
      const { data } = await axios.get('/api/v1/profile');
      this.profile = data.data;
      this.form.name = data.data.name || '';
      this.form.email = data.data.email || '';
      this.form.phone = data.data.phone || '';
    } catch (e) {
      toast('Gagal memuat profil.', 'error');
    } finally {
      this.loading = false;
    }
  },

  async resendEmail() {
    this.resending = true;
    try {
      const { data } = await axios.post('/api/v1/auth/email/resend');
      toast(data.message || 'Link verifikasi dikirim ke email Anda.', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal mengirim email verifikasi.', 'error');
    } finally {
      this.resending = false;
    }
  },

  copyReferral() {
    const code = this.profile?.referral_code;
    if (!code) return;
    navigator.clipboard?.writeText(code)
      .then(() => toast('Kode referral disalin.', 'success'))
      .catch(() => toast('Gagal menyalin.', 'error'));
  },

  async save() {
    this.saving = true;
    try {
      await axios.put('/api/v1/profile', this.form);
      toast('Profil berhasil diperbarui.', 'success');
      const store = Alpine.store('auth');
      if (store.user) {
        store.user.name = this.form.name;
        store.user.email = this.form.email;
        store.setAuth(store.user, store.token);
      }
      await this.fetch();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal memperbarui profil.', 'error');
    } finally {
      this.saving = false;
    }
  },

  async changePassword() {
    if (this.pw.new_password.length < 8) {
      toast('Password baru minimal 8 karakter.', 'error');
      return;
    }
    if (this.pw.new_password !== this.pw.new_password_confirmation) {
      toast('Konfirmasi password tidak cocok.', 'error');
      return;
    }
    this.savingPw = true;
    try {
      await axios.post('/api/v1/profile/change-password', this.pw);
      toast('Password berhasil diperbarui.', 'success');
      this.pw = { current_password: '', new_password: '', new_password_confirmation: '' };
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal mengubah password.', 'error');
    } finally {
      this.savingPw = false;
    }
  },

  async uploadAvatar(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    this.uploading = true;
    try {
      const { data } = await axios.post('/api/v1/profile/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.data?.avatar_url;
      if (url && this.profile) this.profile.avatar = url;
      const store = Alpine.store('auth');
      if (store.user && url) {
        store.user.avatar = url;
        store.setAuth(store.user, store.token);
      }
      toast('Foto profil diperbarui.', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal mengunggah foto.', 'error');
    } finally {
      this.uploading = false;
      event.target.value = '';
    }
  },

  async fetchPasskeys() {
    if (!this.passkeySupported) return;
    this.passkeysLoading = true;
    try {
      const [cfgRes, listRes] = await Promise.all([
        axios.get('/api/v1/auth/passkey/config'),
        axios.get('/api/v1/auth/passkey/list'),
      ]);
      this.passkeyEnabled = !!cfgRes.data?.data?.enabled;
      this.passkeyMax = cfgRes.data?.data?.max_per_user ?? 5;
      this.passkeys = listRes.data?.data?.passkeys || [];
      this.canAddPasskey = !!listRes.data?.data?.can_add;
    } catch {
      this.passkeyEnabled = false;
    } finally {
      this.passkeysLoading = false;
    }
  },

  openPasskeyForm() {
    this.passkeyForm = { label: '', password: '' };
    this.showPasskeyPw = false;
    this.showPasskeyForm = true;
  },

  cancelPasskeyForm() {
    this.showPasskeyForm = false;
    this.passkeyForm = { label: '', password: '' };
  },

  async createPasskey() {
    if (!this.passkeyForm.password) {
      toast('Masukkan password untuk konfirmasi.', 'error');
      return;
    }
    this.creatingPasskey = true;
    try {
      const { data: optRes } = await axios.post('/api/v1/auth/passkey/register/options', {
        password: this.passkeyForm.password,
      });
      const options = optRes.data.options;
      options.publicKey = preparePublicKeyOptions(options.publicKey);
      const credential = await navigator.credentials.create(options);
      const body = credentialToJson(credential);

      await axios.post('/api/v1/auth/passkey/register', {
        password: this.passkeyForm.password,
        label: this.passkeyForm.label || undefined,
        ...body,
      });

      toast('Passkey berhasil disimpan. Anda bisa login tanpa password.', 'success');
      this.cancelPasskeyForm();
      await this.fetchPasskeys();
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        toast('Pembuatan passkey dibatalkan.', 'info');
      } else {
        toast(e.response?.data?.message || e.message || 'Gagal membuat passkey.', 'error');
      }
    } finally {
      this.creatingPasskey = false;
    }
  },

  async removePasskey(pk) {
    const ok = await window.confirmDialog({
      title: 'Hapus Passkey?',
      message: `Hapus passkey "${pk.label}"? Perangkat ini tidak bisa dipakai login lagi.`,
      confirmText: 'Ya, Hapus',
      cancelText: 'Batal',
      variant: 'danger',
    });
    if (!ok) return;

    const password = window.prompt('Masukkan password akun untuk konfirmasi:');
    if (!password) return;

    this.deletingPasskey = pk.id;
    try {
      await axios.delete(`/api/v1/auth/passkey/${pk.id}`, { data: { password } });
      toast('Passkey dihapus.', 'success');
      await this.fetchPasskeys();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal menghapus passkey.', 'error');
    } finally {
      this.deletingPasskey = '';
    }
  },
}));

// ---- Notifications (full page) ------------------------------
Alpine.data('dashboardNotifications', () => ({
  loading: true,
  items: [],
  meta: null,
  page: 1,
  filter: 'all',

  async init() { await this.fetch(); },

  async fetch() {
    this.loading = true;
    try {
      const params = { page: this.page, per_page: 15 };
      if (this.filter === 'unread') params.is_read = 'false';
      const { data } = await axios.get('/api/v1/notifications', { params });
      this.items = data.data || [];
      this.meta = data.meta || null;
      Alpine.store('notifications').unread = data.unread_count ?? 0;
    } catch (e) {
      toast('Gagal memuat notifikasi.', 'error');
    } finally {
      this.loading = false;
    }
  },

  async setFilter(f) {
    if (this.filter === f) return;
    this.filter = f;
    this.page = 1;
    await this.fetch();
  },

  async go(p) {
    if (p < 1 || (this.meta && p > this.meta.last_page)) return;
    this.page = p;
    await this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },

  async markRead(n) {
    if (n.is_read) return;
    try {
      await axios.post(`/api/v1/notifications/${n.id}/read`);
      n.is_read = true;
      Alpine.store('notifications').fetchUnread();
    } catch { /* silent */ }
  },

  async markAllRead() {
    try {
      await axios.post('/api/v1/notifications/read-all');
      this.items.forEach((n) => { n.is_read = true; });
      Alpine.store('notifications').unread = 0;
      toast('Semua notifikasi ditandai terbaca.', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal.', 'error');
    }
  },

  open(n) {
    this.markRead(n);
    const href = resolveNotificationWebUrl(n);
    if (href) window.location.href = href;
  },
}));

// ---- Gamification (poin / XP / level) -----------------------
Alpine.data('dashboardGamification', () => ({
  loading: true,
  summary: null,
  tab: 'quests',
  coinHistory: [],
  xpHistory: [],
  coinPage: 1,
  xpPage: 1,
  claiming: '',
  applyingCreator: false,
  creatorMessage: '',
  convertCoins: null,
  converting: false,
  showConvertAppModal: false,

  // YouTube quest player
  ytActiveId: null,
  ytPlaying: false,
  ytPlayer: null,
  ytHeartbeatTimer: null,
  ytLocalTimer: null,
  ytApiReady: false,
  ytPendingChannel: null,
  ytBaseSeconds: 0,
  ytPlayStartedAt: null,

  get convertPreviewRp() {
    return null;
  },

  get level1Quests() {
    return (this.summary?.quests || []).filter((q) => q.group === 'level_1');
  },
  get level2Quests() {
    return (this.summary?.quests || []).filter((q) => q.group === 'level_2');
  },
  get level3Quests() {
    return (this.summary?.quests || []).filter((q) => q.group === 'level_3');
  },
  get currentMembershipCardUrl() {
    if (!this.summary) return null;
    if (this.summary.membership_card_url) return this.summary.membership_card_url;
    const level = Number(this.summary.level ?? 0);
    const lv = (this.summary.levels || []).find((l) => Number(l.level) === level);
    return lv?.membership_card_url || null;
  },
  get pendingLevel1() {
    return this.level1Quests.filter((q) => !q.completed);
  },
  get pendingLevel2() {
    return this.level2Quests.filter((q) => !q.completed);
  },

  async init() {
    await this.fetchSummary();
    this.ensureYtApi();
  },

  async fetchSummary() {
    try {
      const { data } = await axios.get('/api/v1/gamification/dashboard');
      this.summary = data.data;
    } catch (e) {
      toast('Gagal memuat data poin.', 'error');
    } finally {
      this.loading = false;
    }
  },

  openConvertAppModal() {
    this.showConvertAppModal = true;
  },

  /** Web: konversi hanya di app — modal download. API tetap untuk mobile. */
  convertToWallet() {
    this.openConvertAppModal();
  },

  async fetchCoinHistory() {
    try {
      const { data } = await axios.get('/api/v1/gamification/coins/history', { params: { page: this.coinPage } });
      this.coinHistory = data.data?.transactions || [];
    } catch { /* silent */ }
  },

  async fetchXpHistory() {
    try {
      const { data } = await axios.get('/api/v1/gamification/xp/history', { params: { page: this.xpPage } });
      this.xpHistory = data.data?.transactions || [];
    } catch { /* silent */ }
  },

  async switchTab(t) {
    this.tab = t;
    if (t === 'coins' && !this.coinHistory.length) await this.fetchCoinHistory();
    if (t === 'xp' && !this.xpHistory.length) await this.fetchXpHistory();
  },

  copyReferral() {
    const url = this.summary?.referral_share_url;
    if (!url) return;
    navigator.clipboard?.writeText(url).then(() => toast('Link referral disalin.', 'success'));
  },

  async claimTreasureHunt() {
    if (this.claiming === 'treasure_hunt') return;
    this.claiming = 'treasure_hunt';
    try {
      const { data } = await axios.post('/api/v1/treasure-hunt/claim');
      toast(data.message || data.data?.message || 'Hadiah Treasure Hunt diklaim!', 'success');
      await this.fetchSummary();
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal klaim Treasure Hunt.', 'error');
    } finally {
      this.claiming = '';
    }
  },

  ytProgress(ch) {
    const req = ch.required_seconds || this.summary?.youtube_min_watch_seconds || 180;
    return Math.min(100, Math.round((this.ytDisplaySeconds(ch) / Math.max(1, req)) * 100));
  },

  ytDisplaySeconds(ch) {
    let sec = ch.watched_seconds || 0;
    if (this.ytPlaying && this.ytActiveId === ch.id && this.ytPlayStartedAt) {
      const extra = Math.floor((Date.now() - this.ytPlayStartedAt) / 1000);
      const req = ch.required_seconds || 180;
      sec = Math.min(req, this.ytBaseSeconds + Math.max(0, extra));
    }
    return sec;
  },

  ytTimeLabel(ch) {
    const w = this.ytDisplaySeconds(ch);
    const r = ch.required_seconds || this.summary?.youtube_min_watch_seconds || 180;
    const fmt = (s) => {
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${m}:${String(sec).padStart(2, '0')}`;
    };
    return `${fmt(w)} / ${fmt(r)}`;
  },

  ensureYtApi() {
    if (window.YT?.Player) {
      this.ytApiReady = true;
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      this.ytApiReady = true;
      if (typeof prev === 'function') prev();
      if (this.ytPendingChannel) {
        const ch = this.ytPendingChannel;
        this.ytPendingChannel = null;
        this.mountYtPlayer(ch);
      }
    };
    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  },

  toggleYtPlayer(ch) {
    if (this.ytActiveId === ch.id) {
      this.destroyYtPlayer();
      return;
    }
    this.destroyYtPlayer();
    this.ytActiveId = ch.id;
    this.$nextTick(() => {
      if (this.ytApiReady || window.YT?.Player) {
        this.mountYtPlayer(ch);
      } else {
        this.ytPendingChannel = ch;
        this.ensureYtApi();
      }
    });
  },

  mountYtPlayer(ch) {
    if (!ch.video_id) return;
    const elId = `yt-player-${ch.id}`;
    let el = document.getElementById(elId);
    if (!el || !window.YT?.Player) return;

    // YT.Player replaces the node; restore a clean mount target inside the frame
    if (el.tagName === 'IFRAME') {
      const fresh = document.createElement('div');
      fresh.id = elId;
      el.replaceWith(fresh);
      el = fresh;
    } else {
      el.innerHTML = '';
    }

    this.ytPlayer = new window.YT.Player(elId, {
      videoId: ch.video_id,
      width: '100%',
      height: '100%',
      playerVars: {
        playsinline: 1,
        rel: 0,
        modestbranding: 1,
        origin: window.location.origin,
      },
      events: {
        onStateChange: (e) => this.onYtStateChange(e, ch),
      },
    });
  },

  onYtStateChange(e, ch) {
    const Playing = window.YT?.PlayerState?.PLAYING;
    const Paused = window.YT?.PlayerState?.PAUSED;
    const Ended = window.YT?.PlayerState?.ENDED;
    const Buffering = window.YT?.PlayerState?.BUFFERING;

    if (e.data === Playing) {
      this.ytPlaying = true;
      this.startYtTimers(ch);
      this.sendYtHeartbeat(ch, true);
    } else if (e.data === Paused || e.data === Ended) {
      this.ytPlaying = false;
      this.stopYtTimers();
      this.sendYtHeartbeat(ch, false);
    } else if (e.data === Buffering) {
      // keep previous playing flag; don't pause clock for short buffers
    }
  },

  startYtTimers(ch) {
    this.stopYtTimers(false);
    const current = (this.summary?.youtube_channels || []).find((c) => c.id === ch.id);
    this.ytBaseSeconds = current?.watched_seconds || 0;
    this.ytPlayStartedAt = Date.now();
    this.ytHeartbeatTimer = setInterval(() => {
      if (this.ytPlaying) this.sendYtHeartbeat(ch, true);
    }, 5000);
    // UI refresh only — seconds come from base + elapsed
    this.ytLocalTimer = setInterval(() => {
      /* Alpine reactivity: touch a field so progress re-renders */
      this.ytPlayStartedAt = this.ytPlayStartedAt;
    }, 500);
  },

  stopYtTimers(clearPlayClock = true) {
    if (this.ytHeartbeatTimer) {
      clearInterval(this.ytHeartbeatTimer);
      this.ytHeartbeatTimer = null;
    }
    if (this.ytLocalTimer) {
      clearInterval(this.ytLocalTimer);
      this.ytLocalTimer = null;
    }
    if (clearPlayClock) {
      this.ytPlayStartedAt = null;
    }
  },

  async sendYtHeartbeat(ch, playing) {
    try {
      const { data } = await axios.post('/api/v1/gamification/youtube/heartbeat', {
        channel_id: ch.id,
        playing: !!playing,
      });
      const updated = data.data?.channel;
      if (updated && this.summary?.youtube_channels) {
        const idx = this.summary.youtube_channels.findIndex((c) => c.id === updated.id);
        if (idx >= 0) this.summary.youtube_channels[idx] = updated;
        if (playing && this.ytActiveId === updated.id) {
          this.ytBaseSeconds = updated.watched_seconds || 0;
          this.ytPlayStartedAt = Date.now();
        }
      }
    } catch { /* silent — avoid toast spam while watching */ }
  },

  destroyYtPlayer() {
    const wasId = this.ytActiveId;
    const ch = (this.summary?.youtube_channels || []).find((c) => c.id === wasId);
    this.stopYtTimers();
    if (this.ytPlaying && ch) {
      this.sendYtHeartbeat(ch, false);
    }
    this.ytPlaying = false;
    try {
      this.ytPlayer?.destroy?.();
    } catch { /* ignore */ }
    this.ytPlayer = null;
    this.ytActiveId = null;
    this.ytPendingChannel = null;
  },

  async claimYoutube(ch) {
    this.claiming = 'yt' + ch.id;
    try {
      const { data } = await axios.post('/api/v1/gamification/youtube/claim', {
        channel_id: ch.id,
      });
      const updated = data.data?.channel;
      if (updated && this.summary?.youtube_channels) {
        const idx = this.summary.youtube_channels.findIndex((c) => c.id === updated.id);
        if (idx >= 0) this.summary.youtube_channels[idx] = updated;
      }
      if (data.data?.daily_quests) this.summary.daily_quests = data.data.daily_quests;
      if (data.data?.point_balance != null) this.summary.point_balance = data.data.point_balance;
      if (data.data?.youtube_claimed_today) {
        this.summary.youtube_claimed_today = data.data.youtube_claimed_today;
      }
      toast(data.message || 'Poin ditambahkan!', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal klaim quest YouTube.', 'error');
    } finally {
      this.claiming = '';
    }
  },

  async claimDaily(code) {
    this.claiming = code;
    try {
      const quest = (this.summary?.daily_quests || []).find((d) => d.code === code);
      let adSessionToken = null;
      if (quest?.ad_required && quest?.ad_placement) {
        // Soft gate: user should use rewarded slot first; attempt claim and show message if blocked
        try {
          const start = await axios.post('/api/v1/ads/reward/start', {
            placement_key: quest.ad_placement,
            platform: 'web',
          });
          if (start.data?.data?.session_token) {
            const min = start.data.data.min_watch_sec || 15;
            await new Promise((r) => setTimeout(r, Math.min(min, 15) * 1000));
            const done = await axios.post('/api/v1/ads/reward/complete', {
              session_token: start.data.data.session_token,
              platform: 'web',
            });
            if (done.data?.data?.ok !== false) {
              adSessionToken = start.data.data.session_token;
            }
          }
        } catch (adErr) {
          toast(adErr.response?.data?.message || 'Tonton iklan dulu (panel di atas) lalu klaim ulang.', 'error');
          this.claiming = '';
          return;
        }
      }
      const { data } = await axios.post(`/api/v1/gamification/daily/${code}`, {
        platform: 'web',
        ad_session_token: adSessionToken,
      });
      this.summary.daily_quests = data.data?.daily_quests || this.summary.daily_quests;
      this.summary.weekly_quests = data.data?.weekly_quests || this.summary.weekly_quests;
      this.summary.monthly_quests = data.data?.monthly_quests || this.summary.monthly_quests;
      this.summary.point_balance = data.data?.point_balance ?? this.summary.point_balance;
      if (data.data?.xp_balance != null) this.summary.xp_balance = data.data.xp_balance;
      toast(data.message || 'Poin ditambahkan!', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal klaim quest.', 'error');
    } finally {
      this.claiming = '';
    }
  },

  async claimPeriod(q) {
    if (!q?.code) return;
    this.claiming = q.code;
    try {
      let adSessionToken = null;
      if (q.ad_required && q.ad_placement) {
        try {
          const start = await axios.post('/api/v1/ads/reward/start', {
            placement_key: q.ad_placement,
            platform: 'web',
          });
          if (start.data?.data?.session_token) {
            const min = start.data.data.min_watch_sec || 15;
            await new Promise((r) => setTimeout(r, Math.min(min, 15) * 1000));
            await axios.post('/api/v1/ads/reward/complete', {
              session_token: start.data.data.session_token,
              platform: 'web',
            });
            adSessionToken = start.data.data.session_token;
          }
        } catch (adErr) {
          toast(adErr.response?.data?.message || 'Tonton iklan dulu sebelum klaim.', 'error');
          this.claiming = '';
          return;
        }
      }
      const { data } = await axios.post(`/api/v1/gamification/period/${q.code}/claim`, {
        platform: 'web',
        ad_session_token: adSessionToken,
      });
      this.summary.weekly_quests = data.data?.weekly_quests || this.summary.weekly_quests;
      this.summary.monthly_quests = data.data?.monthly_quests || this.summary.monthly_quests;
      this.summary.quests = data.data?.quests || this.summary.quests;
      this.summary.point_balance = data.data?.point_balance ?? this.summary.point_balance;
      if (data.data?.xp_balance != null) this.summary.xp_balance = data.data.xp_balance;
      toast(data.message || 'Quest periode diklaim!', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal klaim quest periode.', 'error');
    } finally {
      this.claiming = '';
    }
  },

  async applyCreator() {
    this.applyingCreator = true;
    try {
      const { data } = await axios.post('/api/v1/creator/apply', {
        message: this.creatorMessage || null,
      });
      this.summary.creator_application = data.data?.creator || data.data;
      this.creatorMessage = '';
      toast(data.message || 'Pengajuan Creator terkirim.', 'success');
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal mengajukan Creator.', 'error');
    } finally {
      this.applyingCreator = false;
    }
  },
}));

// ---- Flower history (public page, token auth) -----------------
Alpine.data('flowerHistory', () => ({
  loading: true,
  cards: [],
  meta: null,
  page: 1,

  init() {
    if (!Alpine.store('auth').isLoggedIn) {
      this.loading = false;
      return;
    }
    this.fetch();
  },

  loginUrl() {
    return '/login?redirect=' + encodeURIComponent(window.location.pathname);
  },

  async fetch() {
    this.loading = true;
    try {
      const { data } = await axios.get('/api/v1/account/flowers', {
        params: { page: this.page },
      });
      this.cards = data.data || [];
      this.meta = data.meta || null;
    } catch (e) {
      toast('Gagal memuat riwayat.', 'error');
    } finally {
      this.loading = false;
    }
  },

  async go(p) {
    if (p < 1 || (this.meta && p > this.meta.last_page)) return;
    this.page = p;
    await this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
}));

Alpine.data('dashboardWishlist', () => ({
  loading: true,
  items: [],
  meta: null,
  page: 1,
  busyId: null,

  async init() {
    await this.fetch();
    Alpine.store('wishlist')?.syncIds();
  },

  async fetch() {
    this.loading = true;
    try {
      const { data } = await axios.get('/api/v1/wishlist', {
        params: { page: this.page, per_page: 12 },
      });
      this.items = data.data || [];
      this.meta = data.meta || null;
    } catch (e) {
      toast('Gagal memuat wishlist.', 'error');
    } finally {
      this.loading = false;
    }
  },

  async go(p) {
    if (p < 1 || (this.meta && p > this.meta.last_page)) return;
    this.page = p;
    await this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },

  async remove(item) {
    if (!item?.product_id) return;
    this.busyId = item.product_id;
    try {
      await axios.delete(`/api/v1/wishlist/${item.product_id}`);
      this.items = this.items.filter((i) => i.product_id !== item.product_id);
      Alpine.store('wishlist')?.syncIds();
      toast('Dihapus dari wishlist.', 'success');
      if (!this.items.length && this.page > 1) {
        this.page -= 1;
        await this.fetch();
      }
    } catch (e) {
      toast(e?.response?.data?.message || 'Gagal menghapus.', 'error');
    } finally {
      this.busyId = null;
    }
  },
}));
